/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import '@neo4j-ndl/base/lib/neo4j-ds-styles.css';

import {
  Dialog,
  FilledButton,
  OutlinedButton,
  Spotlight,
  SpotlightTarget,
  Typography,
  useNeedleTheme,
  useSpotlightContext,
} from '@neo4j-ndl/react';
import { useEffect, useState } from 'react';

const Component = (props: { isChromatic: boolean }) => {
  const { themeClassName } = useNeedleTheme();
  const { setIsOpen, setActiveSpotlight } = useSpotlightContext();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(props.isChromatic);
  const targetId = `test-target-${themeClassName}`;

  useEffect(() => {
    if (props.isChromatic && isModalOpen) {
      setActiveSpotlight(targetId);
      setIsOpen(true);
    }
  }, [isModalOpen, setActiveSpotlight, setIsOpen, targetId, props.isChromatic]);

  return (
    <div className="n-flex n-justify-center n-gap-token-64">
      <FilledButton onClick={() => setIsModalOpen(true)}>
        Open Dialog
      </FilledButton>
      <Dialog
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        className={themeClassName}
      >
        <Dialog.Header>Using Spotlight inside a dialog</Dialog.Header>
        <Dialog.Content className="n-flex n-flex-col n-gap-token-32">
          <Typography variant="body-medium">
            Spotlight and SpotlightTarget should behave automatically correct
            when used inside a dialog. If positioning is not on top of
            everything, you might want to override the
            <code>--spotlight-z-index</code> css variable.
          </Typography>
          <div className="n-flex n-justify-center n-gap-token-64">
            <FilledButton onClick={() => setActiveSpotlight(targetId)}>
              Activate Spotlight
            </FilledButton>
            <SpotlightTarget
              id={targetId}
              hasPulse={!props.isChromatic}
              asChild
              indicatorVariant="border"
            >
              <FilledButton onClick={() => alert('Target button clicked')}>
                Target
              </FilledButton>
            </SpotlightTarget>
          </div>
        </Dialog.Content>
        <Spotlight
          placement="bottom-end-top-end"
          target={targetId}
          actions={
            <OutlinedButton
              onClick={() => {
                setIsOpen(false);
                setActiveSpotlight(null);
              }}
            >
              Close
            </OutlinedButton>
          }
        >
          <Spotlight.Header>This is a header</Spotlight.Header>
          <Spotlight.Body>This is content</Spotlight.Body>
        </Spotlight>
      </Dialog>
    </div>
  );
};

export default Component;
